import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Plus,
    Play,
    Pause,
    X,
    Trash2,
    Calendar,
    CheckCircle2,
    AlertCircle,
} from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import {
    SourcePolicyDestination,
    ScheduleChangeTracking,
} from "@/components/SchedulePolicyCards";
import StorageEndpointPicker from "@/components/StorageEndpointPicker";

// A "nas"/"s3"/"gcs"/"azure"-kind agent record is just stored connection
// details — nothing installs software on it, so it can't run a real fs-list
// or fs-mkdir itself. Browsing/creating folders on its behalf needs a real
// installed agent to connect for it (same pattern as Data Migration).
const CONNECTION_KINDS = new Set(["nas", "s3", "gcs", "azure"]);

const ROLE_RANK = { viewer: 1, auditor: 2, engineer: 3, admin: 4, superadmin: 5 };
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Schedules() {
    const { user } = useAuth();
    const canEdit = (ROLE_RANK[user?.role] || 0) >= ROLE_RANK.engineer;
    const [schedules, setSchedules] = useState([]);
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [selected, setSelected] = useState(null);
    const [runs, setRuns] = useState([]);

    const refresh = async () => {
        try {
            const [s, a] = await Promise.all([http.get("/schedules"), http.get("/agents")]);
            setSchedules(s.data);
            setAgents(a.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
        const iv = setInterval(refresh, 10000);
        return () => clearInterval(iv);
    }, []);

    // Poll runs for the currently-selected schedule.
    useEffect(() => {
        if (!selected?.id) {
            setRuns([]);
            return;
        }
        let cancelled = false;
        const pull = async () => {
            try {
                const { data } = await http.get(`/schedules/${selected.id}/runs?limit=10`);
                if (!cancelled) setRuns(data);
            } catch (err) {
                // Polling failures are transient — toasting on every 6s
                // tick would spam the user. Log to the console so it's
                // visible during dev / when DevTools is open.
                console.warn("schedule-runs poll failed:", err?.message || err);
            }
        };
        pull();
        const iv = setInterval(pull, 6000);
        return () => {
            cancelled = true;
            clearInterval(iv);
        };
    }, [selected?.id]);

    const metrics = useMemo(() => {
        const by = (status) => schedules.filter((s) => s.status === status).length;
        return {
            total: schedules.length,
            active: by("active"),
            paused: by("paused"),
            disabled: by("disabled"),
        };
    }, [schedules]);

    // Status filter tab — purely client-side, doesn't change any API call.
    const [statusFilter, setStatusFilter] = useState("all");
    const filteredSchedules = useMemo(() => {
        if (statusFilter === "all") return schedules;
        return schedules.filter((s) => s.status === statusFilter);
    }, [schedules, statusFilter]);

    const runNow = async (id) => {
        try {
            const { data } = await http.post(`/schedules/${id}/run`);
            toast.success(`Job ${data.job_id.slice(0, 8)}… queued`);
            await refresh();
        } catch (e) {
            toast.error(errMsg(e));
        }
    };

    const setStatus = async (id, status) => {
        try {
            await http.patch(`/schedules/${id}`, { status });
            toast.success(`Schedule ${status}`);
            await refresh();
        } catch (e) {
            toast.error(errMsg(e));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this schedule? Historic jobs keep running.")) return;
        try {
            await http.delete(`/schedules/${id}`);
            if (selected?.id === id) setSelected(null);
            toast.success("Schedule deleted");
            await refresh();
        } catch (e) {
            toast.error(errMsg(e));
        }
    };

    const fmt = (iso) => {
        if (!iso) return "—";
        try {
            return new Date(iso).toLocaleString();
        } catch {
            return iso;
        }
    };

    const describeFreq = (s) => {
        if (s.frequency === "daily") return `Daily · ${s.time_of_day}`;
        if (s.frequency === "weekly")
            return `Weekly · ${DOW[s.day_of_week ?? 0]} ${s.time_of_day}`;
        if (s.frequency === "monthly")
            return `Monthly · day ${s.day_of_month} ${s.time_of_day}`;
        if (s.frequency === "interval") return `Every ${s.interval_minutes} min`;
        return s.frequency;
    };

    return (
        <div className="mx-auto max-w-[1600px] space-y-5 p-6">
            <PageHero
                eyebrow="MoovBytes Backups"
                title="Automate recurring backups"
                description="Source → Policy → Destination. Schedules fire automatically and capture change-tracking deltas you can audit per run."
                icon={Calendar}
                tint="#5b5ef0"
                testId="sched-page-hero"
                actions={
                    canEdit && (
                        <button
                            onClick={() => setCreating(true)}
                            data-testid="schedule-create-btn"
                            className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-[#5b5ef0] to-[#7c5cff] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-[#5b5ef0]/25 transition-all hover:shadow-xl hover:shadow-[#5b5ef0]/40"
                        >
                            <Plus size={14} /> New Backup
                        </button>
                    )
                }
            />

            {/* Metrics */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="schedule-metrics">
                <Metric label="Total" value={metrics.total} icon={Calendar} color="#5b5ef0" />
                <Metric label="Active" value={metrics.active} icon={CheckCircle2} color="#16a34a" />
                <Metric label="Paused" value={metrics.paused} icon={Pause} color="#f59e0b" />
                <Metric label="Disabled" value={metrics.disabled} icon={AlertCircle} color="#94a3b8" />
            </div>

            {/* Status filter tabs — pure UI, no API impact. */}
            <div className="flex flex-wrap items-center gap-1 rounded-xl border border-border bg-surface p-1 text-xs" data-testid="schedule-filter-tabs">
                {[
                    { v: "all",      label: "All",      count: metrics.total },
                    { v: "active",   label: "Active",   count: metrics.active },
                    { v: "paused",   label: "Paused",   count: metrics.paused },
                    { v: "disabled", label: "Disabled", count: metrics.disabled },
                ].map((t) => {
                    const active = statusFilter === t.v;
                    return (
                        <button
                            key={t.v}
                            type="button"
                            onClick={() => setStatusFilter(t.v)}
                            data-testid={`schedule-filter-${t.v}`}
                            className={[
                                "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] transition-colors",
                                active
                                    ? "bg-[#5b5ef0]/15 text-[#5b5ef0]"
                                    : "text-muted-foreground hover:bg-muted/40 hover:text-foreground",
                            ].join(" ")}
                        >
                            {t.label}
                            <span className={[
                                "rounded-full px-1.5 py-0.5 font-mono text-[9px] tabular-nums",
                                active ? "bg-[#5b5ef0]/20" : "bg-muted/60",
                            ].join(" ")}>
                                {t.count}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
                {/* Table */}
                <div className="rounded-xl border border-border bg-surface">
                    <div className="border-b border-border px-4 py-3">
                        <h3 className="font-display text-lg font-bold">All schedules</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                    <th className="px-4 py-2">Name</th>
                                    <th className="px-4 py-2">Frequency</th>
                                    <th className="px-4 py-2">Next run</th>
                                    <th className="px-4 py-2">Last run</th>
                                    <th className="px-4 py-2">Status</th>
                                    <th className="px-4 py-2 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {loading && (
                                    <tr>
                                        <td colSpan={6} className="py-8 text-center text-xs text-muted-foreground">
                                            Loading…
                                        </td>
                                    </tr>
                                )}
                                {!loading && filteredSchedules.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="py-12 text-center text-xs text-muted-foreground">
                                            {schedules.length === 0 ? (
                                                <>No schedules yet.{" "}
                                                    {canEdit && (
                                                        <button
                                                            onClick={() => setCreating(true)}
                                                            className="font-semibold text-[#5b5ef0] underline"
                                                        >
                                                            Create one
                                                        </button>
                                                    )}
                                                </>
                                            ) : (
                                                <>No {statusFilter} schedules match the current filter.</>
                                            )}
                                        </td>
                                    </tr>
                                )}
                                {filteredSchedules.map((s) => (
                                    <tr
                                        key={s.id}
                                        onClick={() => setSelected(s)}
                                        className={
                                            "cursor-pointer transition-colors hover:bg-muted/40 " +
                                            (selected?.id === s.id ? "bg-muted/60" : "")
                                        }
                                        data-testid={`sched-row-${s.id}`}
                                    >
                                        <td className="px-4 py-3">
                                            <div className="font-semibold">{s.name}</div>
                                            <div className="truncate font-mono text-[10px] text-muted-foreground">
                                                {s.job_template?.name}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-xs">{describeFreq(s)}</td>
                                        <td className="px-4 py-3 font-mono text-[11px]">
                                            {s.status === "active" ? fmt(s.next_run_at) : "—"}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                                            {fmt(s.last_run_at)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <StatusPill status={s.status} />
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div
                                                className="inline-flex items-center gap-1"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {canEdit && (
                                                    <>
                                                        <IconBtn
                                                            title="Run now"
                                                            onClick={() => runNow(s.id)}
                                                            testId={`sched-run-${s.id}`}
                                                        >
                                                            <Play size={12} />
                                                        </IconBtn>
                                                        {s.status === "active" ? (
                                                            <IconBtn
                                                                title="Pause"
                                                                onClick={() => setStatus(s.id, "paused")}
                                                                testId={`sched-pause-${s.id}`}
                                                            >
                                                                <Pause size={12} />
                                                            </IconBtn>
                                                        ) : s.status === "paused" ? (
                                                            <IconBtn
                                                                title="Resume"
                                                                onClick={() => setStatus(s.id, "active")}
                                                                testId={`sched-resume-${s.id}`}
                                                            >
                                                                <Play size={12} />
                                                            </IconBtn>
                                                        ) : null}
                                                        {s.status !== "disabled" && (
                                                            <IconBtn
                                                                title="Disable"
                                                                onClick={() => setStatus(s.id, "disabled")}
                                                                testId={`sched-disable-${s.id}`}
                                                            >
                                                                <X size={12} />
                                                            </IconBtn>
                                                        )}
                                                        <IconBtn
                                                            title="Delete"
                                                            danger
                                                            onClick={() => remove(s.id)}
                                                            testId={`sched-delete-${s.id}`}
                                                        >
                                                            <Trash2 size={12} />
                                                        </IconBtn>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Detail panel */}
                <div className="rounded-xl border border-border bg-surface p-4" data-testid="schedule-panel">
                    {selected ? (
                        <>
                            <div className="mb-3 flex items-center justify-between">
                                <h3 className="font-display text-lg font-bold truncate" title={selected.name}>{selected.name}</h3>
                                <StatusPill status={selected.status} />
                            </div>

                            {/* High-density timing summary */}
                            <div className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-background/40 p-2.5 text-[11px]">
                                <div>
                                    <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Next run</div>
                                    <div className="mt-0.5 font-mono tabular-nums">{fmt(selected.next_run_at)}</div>
                                </div>
                                <div>
                                    <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Last run</div>
                                    <div className="mt-0.5 font-mono tabular-nums">{fmt(selected.last_run_at)}</div>
                                </div>
                                <div>
                                    <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Run count</div>
                                    <div className="mt-0.5 font-mono tabular-nums">{selected.run_count || 0}</div>
                                </div>
                                <div>
                                    <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">Skipped</div>
                                    <div className="mt-0.5 font-mono tabular-nums">{selected.skip_count || 0}</div>
                                </div>
                            </div>

                            {/* Source → Policy → Destination tri-card. On the
                                narrow right-panel width this collapses
                                vertically; on wide screens it sits inline. */}
                            <div className="mt-3">
                                <SourcePolicyDestination
                                    schedule={selected}
                                    agents={agents}
                                    describeFreq={describeFreq}
                                />
                            </div>

                            {canEdit && (
                                <div className="mt-3 flex gap-2">
                                    <button
                                        onClick={() => runNow(selected.id)}
                                        data-testid={`sched-detail-run-${selected.id}`}
                                        className="flex-1 rounded-md bg-gradient-to-r from-[#5b5ef0] to-[#7c5cff] px-3 py-2 text-xs font-semibold text-white shadow-md transition-all hover:shadow-lg"
                                    >
                                        Run now
                                    </button>
                                </div>
                            )}

                            {/* Change tracking visualization */}
                            <div className="mt-4">
                                <ScheduleChangeTracking scheduleId={selected.id} />
                            </div>

                            {/* Run history — preserved verbatim from the
                                original layout so existing test IDs and
                                user expectations are unchanged. */}
                            <div className="mt-4 border-t border-border pt-3">
                                <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Recent runs
                                </div>
                                {runs.length === 0 ? (
                                    <div className="py-4 text-center text-[11px] text-muted-foreground">
                                        No runs yet.
                                    </div>
                                ) : (
                                    <ul className="space-y-1" data-testid="schedule-runs">
                                        {runs.map((r) => (
                                            <li
                                                key={r.id}
                                                className="flex items-center justify-between rounded-md border border-border bg-background px-2.5 py-1.5 text-[11px]"
                                            >
                                                <span
                                                    className={
                                                        "rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase " +
                                                        (r.status === "fired"
                                                            ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]"
                                                            : "bg-[#f59e0b]/15 text-[#b45309]")
                                                    }
                                                >
                                                    {r.status}
                                                </span>
                                                {r.job_id ? (
                                                    <Link
                                                        to={`/jobs/${r.job_id}`}
                                                        className="truncate font-mono text-[10px] text-[#5b5ef0] hover:underline"
                                                    >
                                                        {r.job_id.slice(0, 8)}…
                                                    </Link>
                                                ) : (
                                                    <span className="font-mono text-[10px] text-muted-foreground">
                                                        skipped
                                                    </span>
                                                )}
                                                <span className="font-mono text-[10px] text-muted-foreground">
                                                    {fmt(r.run_time)}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="py-12 text-center text-xs text-muted-foreground">
                            Select a schedule to see details.
                        </div>
                    )}
                </div>
            </div>

            {creating && (
                <CreateScheduleModal
                    agents={agents}
                    onClose={() => setCreating(false)}
                    onCreated={async () => {
                        setCreating(false);
                        await refresh();
                    }}
                />
            )}
        </div>
    );
}

function Metric({ label, value, icon: Icon, color }) {
    return (
        <div className="flex items-center gap-3 rounded-xl border border-border bg-surface p-4">
            <div
                className="flex h-10 w-10 items-center justify-center rounded-lg"
                style={{ background: `${color}22`, color }}
            >
                <Icon size={18} />
            </div>
            <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {label}
                </div>
                <div className="font-display text-2xl font-bold tabular-nums">{value}</div>
            </div>
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        active: ["bg-[hsl(var(--success))]/15", "text-[hsl(var(--success))]"],
        paused: ["bg-[#f59e0b]/15", "text-[#b45309]"],
        disabled: ["bg-muted", "text-muted-foreground"],
    };
    const [bg, fg] = map[status] || map.disabled;
    return (
        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${bg} ${fg}`}>
            {status}
        </span>
    );
}

function IconBtn({ children, onClick, title, danger, testId }) {
    return (
        <button
            onClick={onClick}
            title={title}
            data-testid={testId}
            className={
                "rounded-md border border-border p-1.5 transition-colors " +
                (danger
                    ? "hover:border-[hsl(var(--destructive))] hover:text-[hsl(var(--destructive))]"
                    : "hover:border-foreground/30")
            }
        >
            {children}
        </button>
    );
}

function DetailRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center justify-between py-1.5 text-xs">
            <div className="flex items-center gap-1.5 text-muted-foreground">
                <Icon size={11} /> {label}
            </div>
            <div className="max-w-[65%] truncate text-right font-medium">{value}</div>
        </div>
    );
}

// ---- Create Modal ---------------------------------------------------
function CreateScheduleModal({ agents: initialAgents, onClose, onCreated }) {
    const [agents, setAgents] = useState(initialAgents);
    // An agent installed via the standard installer always registers with
    // role="both" (it can serve as either end) -- a strict equality check
    // here excluded every real installed agent from BOTH lists, so no
    // device ever showed up when scheduling a backup. Matches the same
    // "source" || "destination" || "both" || !role pattern already used in
    // MigratePC.jsx/MigrateServer.jsx's own source/destination pickers.
    //
    // CONNECTION_KINDS (nas/s3/gcs/azure) are unconditional on TOP of
    // that: these are passive storage records, not installed software --
    // "role" there just reflects whichever side you happened to pick when
    // you first connected it (ConnectStorageModal), not a real constraint
    // on what it can be used for. Reported: a NAS connected as a source
    // never showed up as an available backup destination even though
    // it's perfectly valid as one. A real PC/server agent's role still
    // matters (that reflects what the installed Agent is actually
    // configured to do), so only storage connections get this exemption.
    const sources = agents.filter((a) => CONNECTION_KINDS.has(a.kind) || a.role === "source" || a.role === "both" || !a.role);
    const destinations = agents.filter((a) => CONNECTION_KINDS.has(a.kind) || a.role === "destination" || a.role === "both" || !a.role);
    const [workerId, setWorkerId] = useState("");

    const [form, setForm] = useState({
        name: "",
        frequency: "daily",
        time_of_day: "02:00",
        day_of_week: 0,
        day_of_month: 1,
        interval_minutes: 60,
        tpl_name: "Scheduled sync",
        source_agent_id: sources[0]?.id || "",
        dest_agent_id: destinations[0]?.id || "",
        // Empty, not a Linux-style placeholder like "/data/source" -- that
        // path doesn't exist on a real Windows agent (confirmed: the Browse
        // modal 404'd trying to resolve it as C:\data\source) and silently
        // passing it as FsBrowser's initialPath skipped its own documented
        // "" => agent root (drive listing on Windows) fallback entirely.
        // Browsing from a blank path lets the user navigate to wherever
        // the agent's filesystem actually starts.
        source_path: "",
        destination_path: "",
        migration_type: "incremental",
        total_bytes_gb: 5,
        total_files: 1200,
        retries: 3,
        compression: true,
        encryption: true,
    });
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const set = (k) => (e) =>
        setForm({ ...form, [k]: e && e.target ? e.target.value : e });

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            const body = {
                name: form.name,
                frequency: form.frequency,
                time_of_day: form.time_of_day,
                job_template: {
                    name: form.tpl_name,
                    source_agent_id: form.source_agent_id,
                    dest_agent_id: form.dest_agent_id,
                    source_path: form.source_path,
                    destination_path: form.destination_path,
                    migration_type: form.migration_type,
                    total_files: Number(form.total_files),
                    total_bytes: Math.round(Number(form.total_bytes_gb) * 1024 ** 3),
                    retries: Number(form.retries),
                    compression: form.compression,
                    encryption: form.encryption,
                },
            };
            if (form.frequency === "weekly") body.day_of_week = Number(form.day_of_week);
            if (form.frequency === "monthly") body.day_of_month = Number(form.day_of_month);
            if (form.frequency === "interval") body.interval_minutes = Number(form.interval_minutes);
            await http.post("/schedules", body);
            toast.success("Schedule created");
            onCreated();
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={onClose}
        >
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="relative mx-4 flex max-h-[90vh] w-full max-w-xl flex-col rounded-xl border border-border bg-surface"
                data-testid="create-schedule-modal"
            >
                <div className="shrink-0 border-b border-border p-5">
                    <h3 className="font-display text-lg font-bold">New Schedule</h3>
                    <p className="text-xs text-muted-foreground">
                        Pick a cadence — the scheduler worker sweeps every 30s and enqueues a job automatically.
                    </p>
                </div>
                <div className="flex-1 space-y-4 overflow-y-auto p-5">
                    {err && (
                        <div className="rounded-md border border-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/5 p-2 text-xs text-[hsl(var(--destructive))]">
                            {err}
                        </div>
                    )}
                    <div>
                        <Label>Schedule name</Label>
                        <input
                            required
                            value={form.name}
                            onChange={set("name")}
                            placeholder="Nightly DR sync"
                            data-testid="sched-name"
                            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        />
                    </div>

                    <div>
                        <Label>Frequency</Label>
                        <div className="mt-1 grid grid-cols-4 gap-2">
                            {["daily", "weekly", "monthly", "interval"].map((f) => (
                                <button
                                    key={f}
                                    type="button"
                                    onClick={() => setForm({ ...form, frequency: f })}
                                    data-testid={`sched-freq-${f}`}
                                    className={
                                        "rounded-md border px-3 py-2 text-xs font-medium capitalize transition-colors " +
                                        (form.frequency === f
                                            ? "border-[#5b5ef0] bg-[#5b5ef0]/5 text-[#5b5ef0]"
                                            : "border-border hover:border-foreground/30")
                                    }
                                >
                                    {f}
                                </button>
                            ))}
                        </div>
                    </div>

                    {form.frequency !== "interval" && (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Time (UTC)</Label>
                                <input
                                    type="time"
                                    value={form.time_of_day}
                                    onChange={set("time_of_day")}
                                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                                />
                            </div>
                            {form.frequency === "weekly" && (
                                <div>
                                    <Label>Day of week</Label>
                                    <select
                                        value={form.day_of_week}
                                        onChange={set("day_of_week")}
                                        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                                    >
                                        {DOW.map((d, i) => (
                                            <option key={d} value={i}>
                                                {d}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {form.frequency === "monthly" && (
                                <div>
                                    <Label>Day of month (1–28)</Label>
                                    <input
                                        type="number"
                                        min={1}
                                        max={28}
                                        value={form.day_of_month}
                                        onChange={set("day_of_month")}
                                        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    {form.frequency === "interval" && (
                        <div>
                            <Label>Interval (minutes, min 5)</Label>
                            <input
                                type="number"
                                min={5}
                                max={43200}
                                value={form.interval_minutes}
                                onChange={set("interval_minutes")}
                                className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                            />
                        </div>
                    )}

                    <div className="border-t border-border pt-4">
                        <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            Job template
                        </div>
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <StorageEndpointPicker
                                    label="Source"
                                    role="source"
                                    agents={sources}
                                    onAgentsChange={(updater) => setAgents((prev) => updater(prev))}
                                    agentId={form.source_agent_id}
                                    onAgentChange={(v) => setForm((f) => ({ ...f, source_agent_id: v }))}
                                    path={form.source_path}
                                    onPathChange={(v) => setForm((f) => ({ ...f, source_path: v }))}
                                    workerAgentId={workerId}
                                    testid="sched-source"
                                />
                                <StorageEndpointPicker
                                    label="Destination"
                                    role="destination"
                                    agents={destinations}
                                    onAgentsChange={(updater) => setAgents((prev) => updater(prev))}
                                    agentId={form.dest_agent_id}
                                    onAgentChange={(v) => setForm((f) => ({ ...f, dest_agent_id: v }))}
                                    path={form.destination_path}
                                    onPathChange={(v) => setForm((f) => ({ ...f, destination_path: v }))}
                                    workerAgentId={workerId}
                                    testid="sched-dest"
                                />
                            </div>
                            {(sources.some((a) => CONNECTION_KINDS.has(a.kind)) || destinations.some((a) => CONNECTION_KINDS.has(a.kind))) && (
                                <div>
                                    <Label>Worker agent (for browsing IP/path connections)</Label>
                                    <select
                                        value={workerId}
                                        onChange={(e) => setWorkerId(e.target.value)}
                                        data-testid="sched-worker-agent-select"
                                        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                                    >
                                        <option value="">Pick a worker agent…</option>
                                        {agents.filter((a) => !CONNECTION_KINDS.has(a.kind)).map((a) => (
                                            <option key={a.id} value={a.id}>
                                                {`${a.name || a.hostname || a.id.slice(0, 8)} (${a.kind || "vm"})`}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label>Type</Label>
                                    <select
                                        value={form.migration_type}
                                        onChange={set("migration_type")}
                                        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                                    >
                                        <option value="incremental">Incremental Sync</option>
                                        <option value="full">Full Migration</option>
                                        <option value="mirror">Mirror</option>
                                        <option value="custom">Custom</option>
                                    </select>
                                </div>
                                <div>
                                    <Label>Size (GB)</Label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        min={0.001}
                                        value={form.total_bytes_gb}
                                        onChange={set("total_bytes_gb")}
                                        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="shrink-0 flex items-center justify-end gap-2 border-t border-border p-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:border-foreground/30"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="sched-submit"
                        className="rounded-md bg-gradient-to-r from-[#5b5ef0] to-[#7c5cff] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-[#5b5ef0]/25 disabled:opacity-60"
                    >
                        {busy ? "Creating…" : "Create schedule"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function Label({ children }) {
    return (
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            {children}
        </span>
    );
}
